import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Pencil, Plus, Sparkles, X, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { EVENTS, track } from '@/lib/analytics';

/**
 * Breakdown elements by category (M1.1) with inline editing (M1.2).
 *
 * Mirrors backend/services/breakdown_elements.py. Colours follow the
 * conventional breakdown-sheet highlighter code so an AD who has done this on
 * paper recognises it at a glance.
 */
export const CATEGORIES = [
  { key: 'characters',        label: 'Cast',                colour: 'red' },
  { key: 'extras',            label: 'Extras / junior artists', colour: 'green' },
  { key: 'props',             label: 'Props',               colour: 'violet' },
  { key: 'set_dressing',      label: 'Set dressing',        colour: 'blue' },
  { key: 'wardrobe',          label: 'Wardrobe',            colour: 'teal' },
  { key: 'makeup_hair',       label: 'Makeup & hair',       colour: 'pink' },
  { key: 'vehicles',          label: 'Vehicles',            colour: 'amber' },
  { key: 'animals',           label: 'Animals',             colour: 'lime' },
  { key: 'stunts',            label: 'Stunts',              colour: 'orange' },
  { key: 'vfx',               label: 'VFX',                 colour: 'cyan' },
  { key: 'sfx',               label: 'Special effects',     colour: 'indigo' },
  { key: 'sound_music',       label: 'Sound & music',       colour: 'slate' },
  { key: 'special_equipment', label: 'Special equipment',   colour: 'stone' },
  { key: 'locations',         label: 'Locations',           colour: 'yellow' },
];

// Tailwind can't build class names dynamically, so the palette is explicit.
const TONES = {
  red:    'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/25',
  green:  'bg-green-500/10 text-green-700 dark:text-green-300 border-green-500/25',
  violet: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/25',
  blue:   'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/25',
  teal:   'bg-teal-500/10 text-teal-700 dark:text-teal-300 border-teal-500/25',
  pink:   'bg-pink-500/10 text-pink-700 dark:text-pink-300 border-pink-500/25',
  amber:  'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/25',
  lime:   'bg-lime-500/10 text-lime-700 dark:text-lime-300 border-lime-500/25',
  orange: 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/25',
  cyan:   'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/25',
  indigo: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/25',
  slate:  'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/25',
  stone:  'bg-stone-500/10 text-stone-700 dark:text-stone-300 border-stone-500/25',
  yellow: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 border-yellow-500/25',
};

const DOT = {
  red: 'bg-red-500', green: 'bg-green-500', violet: 'bg-violet-500', blue: 'bg-blue-500', teal: 'bg-teal-500',
  pink: 'bg-pink-500', amber: 'bg-amber-500', lime: 'bg-lime-500', orange: 'bg-orange-500', cyan: 'bg-cyan-500',
  indigo: 'bg-indigo-500', slate: 'bg-slate-500', stone: 'bg-stone-500', yellow: 'bg-yellow-500',
};

/** Build a full elements object from a legacy scene payload if needed. */
export function elementsFromScene(scene) {
  if (scene?.elements && Object.keys(scene.elements).length) return scene.elements;
  const out = {};
  if (scene?.characters?.length) out.characters = scene.characters.map((c) => ({ ...c, source: c.source || 'ai' }));
  if (scene?.props?.length) out.props = scene.props.map((p) => ({ ...p, source: p.source || 'ai' }));
  if (scene?.locations?.length) out.locations = scene.locations.map((l) => ({ ...l, source: l.source || 'ai' }));
  return out;
}

export function countElements(elements) {
  return Object.values(elements || {}).reduce((n, arr) => n + (arr?.length || 0), 0);
}

const Chip = ({ item, tone, editing, onRemove }) => (
  <span
    className={cn('inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] border max-w-full', TONES[tone])}
    title={item.description || undefined}
    data-testid="element-chip"
  >
    <span className="truncate">{item.name}</span>
    {item.count ? <span className="opacity-70">×{item.count}</span> : null}
    {item.source === 'user' ? <Pencil className="size-2.5 opacity-60" aria-label="Added by you" /> : null}
    {editing && (
      <button type="button" onClick={onRemove} className="ml-0.5 opacity-60 hover:opacity-100" aria-label={`Remove ${item.name}`}>
        <X className="size-3" />
      </button>
    )}
  </span>
);

const AddRow = ({ onAdd, isExtras }) => {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [count, setCount] = useState('');
  const submit = (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    const item = { name: n, description: desc.trim(), source: 'user' };
    if (isExtras && count.trim()) item.count = parseInt(count, 10) || undefined;
    onAdd(item);
    setName(''); setDesc(''); setCount('');
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap gap-1.5 mt-2">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="h-8 text-xs w-36" />
      <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Note (optional)" className="h-8 text-xs flex-1 min-w-[8rem]" />
      {isExtras && (
        <Input value={count} onChange={(e) => setCount(e.target.value)} placeholder="#" type="number" min="1" className="h-8 text-xs w-16" />
      )}
      <Button type="submit" size="sm" variant="secondary" className="h-8" disabled={!name.trim()}>
        <Plus className="size-3.5 mr-1" /> Add
      </Button>
    </form>
  );
};

/**
 * Props:
 *   filmId, sceneId
 *   elements       — {category: [{name, description, source, count?}]}
 *   canEdit        — show the edit affordance
 *   onSaved(els)   — called with the server's canonical elements after save
 *   compact        — hide empty categories (default true)
 */
export default function ElementsEditor({ filmId, sceneId, elements, canEdit, onSaved, compact = true }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const current = editing ? draft : elements;
  const total = countElements(current);
  const visible = useMemo(
    () => CATEGORIES.filter((c) => !compact || showAll || editing || (current?.[c.key]?.length > 0)),
    [current, compact, showAll, editing],
  );
  const hiddenCount = CATEGORIES.length - visible.length;

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(elements || {})));
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setDraft(null); };

  const addItem = (cat, item) =>
    setDraft((d) => ({ ...d, [cat]: [...(d[cat] || []), item] }));
  const removeItem = (cat, idx) =>
    setDraft((d) => {
      const arr = [...(d[cat] || [])];
      arr.splice(idx, 1);
      const next = { ...d, [cat]: arr };
      if (!arr.length) delete next[cat];
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API_BASE}/films/${filmId}/scenes/${sceneId}/elements`,
        { elements: draft },
        { headers: getAuthHeaders() },
      );
      if (!data.success) throw new Error(data.error || 'Save failed');
      track(EVENTS.ELEMENTS_EDITED, { film_id: filmId, scene_id: sceneId, element_count: data.element_count });
      onSaved?.(data.elements);
      setEditing(false);
      setDraft(null);
      toast.success('Breakdown updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="elements-editor">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-primary" />
          <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">Breakdown elements</span>
          <span className="text-[11px] text-muted-foreground">· {total}</span>
        </div>
        {canEdit && !editing && (
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={startEdit} data-testid="elements-edit">
            <Pencil className="size-3 mr-1" /> Edit
          </Button>
        )}
        {editing && (
          <div className="flex gap-1.5">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={cancel} disabled={saving}>Cancel</Button>
            <Button size="sm" className="h-7 text-xs" onClick={save} disabled={saving} data-testid="elements-save">
              {saving ? <Loader2 className="size-3 mr-1 animate-spin" /> : <Check className="size-3 mr-1" />} Save
            </Button>
          </div>
        )}
      </div>

      {total === 0 && !editing ? (
        <p className="text-sm text-muted-foreground italic">No elements yet. Analyze the scene or add them by hand.</p>
      ) : (
        <div className="space-y-3">
          {visible.map((cat) => {
            const items = current?.[cat.key] || [];
            return (
              <div key={cat.key} data-testid={`elements-cat-${cat.key}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={cn('size-2 rounded-full', DOT[cat.colour])} />
                  <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{cat.label}</span>
                  {items.length > 0 && <span className="text-[11px] text-muted-foreground">({items.length})</span>}
                </div>
                {items.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {items.map((it, i) => (
                      <Chip key={`${it.name}-${i}`} item={it} tone={cat.colour} editing={editing} onRemove={() => removeItem(cat.key, i)} />
                    ))}
                  </div>
                ) : editing ? null : (
                  <p className="text-xs text-muted-foreground italic">—</p>
                )}
                {editing && <AddRow onAdd={(item) => addItem(cat.key, item)} isExtras={cat.key === 'extras'} />}
              </div>
            );
          })}
        </div>
      )}

      {compact && !editing && hiddenCount > 0 && total > 0 && (
        <button type="button" onClick={() => setShowAll((v) => !v)} className="mt-3 text-[11px] text-muted-foreground hover:text-foreground">
          {showAll ? 'Hide empty categories' : `Show ${hiddenCount} empty categor${hiddenCount === 1 ? 'y' : 'ies'}`}
        </button>
      )}
    </div>
  );
}
